import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../Auth/AuthContext';

function AdvancedScenarioList() {
  // Work scenario categories
  const [workCategories, setWorkCategories] = useState([]);
  // Life scenario categories
  const [lifeCategories, setLifeCategories] = useState([]);
  const [scenariosByCategory, setScenariosByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  
  // eslint-disable-next-line no-unused-vars
  const { currentUser, userData, isAdmin, premiumStatus } = useAuth();
  const categoryRefs = useRef({});
  
  // 获取场景标签
  const getScenarioTags = (scenario) => {
    // 职业标签
    let profession = "General";
    // 场景标签
    let context = "Office";
    // 难度标签
    let difficulty = "Intermediate";
    
    const title = scenario.title_en.toLowerCase();
    const categoryId = scenario.category_id;
    
    // 根据标题和类别确定职业标签
    if (title.includes('manager') || title.includes('lead') || title.includes('boss') || title.includes('executive')) {
      profession = "Management";
    } else if (title.includes('client') || title.includes('customer') || title.includes('service')) {
      profession = "Customer Service";
    } else if (title.includes('sales') || title.includes('negotiation') || title.includes('marketing')) {
      profession = "Sales";
    } else if (title.includes('hr') || title.includes('human resource') || title.includes('recruitment')) {
      profession = "HR";
    } else if (title.includes('technical') || title.includes('engineer') || title.includes('developer')) {
      profession = "Technical";
    } else if (title.includes('admin') || title.includes('assistant')) {
      profession = "Administrative";
    }
    
    // 根据类别确定场景标签
    if (categoryId.includes('workplace') || categoryId.includes('office')) {
      context = "Office";
    } else if (categoryId.includes('meeting') || categoryId.includes('presentation')) {
      context = "Meeting";
    } else if (categoryId.includes('social') || categoryId.includes('network')) {
      context = "Social Event";
    } else if (categoryId.includes('interpersonal') || categoryId.includes('relationship')) {
      context = "Relationship";
    } else if (categoryId.includes('conversation') || categoryId.includes('difficult')) {
      context = "Conversation";
    } else if (categoryId.includes('etiquette')) {
      context = "Etiquette";
    }
    
    // 根据内容复杂度确定难度标签
    if (title.includes('basic') || title.includes('simple') || 
        title.includes('introduction') || title.includes('beginning')) {
      difficulty = "Beginner";
    } else if (title.includes('advanced') || title.includes('expert') || 
               title.includes('complex') || title.includes('challenge')) {
      difficulty = "Advanced";
    } else if (title.includes('master') || title.includes('professional') || 
               title.includes('senior') || title.includes('executive')) {
      difficulty = "Expert";
    }
    
    return { profession, context, difficulty };
  };
  
  // Load scenario data
  useEffect(() => {
    async function loadScenarios() {
      try {
        setLoading(true);
        setError('');
        
        // Try different paths to load data
        const urls = [
          '/data/combinedScenariosData.json',
          './data/combinedScenariosData.json',
          '/combinedScenariosData.json'
        ];
        
        let data = null;
        for (const url of urls) {
          try {
            const response = await fetch(url);
            if (response.ok) {
              data = await response.json();
              console.log(`Successfully loaded data from ${url}`);
              break;
            }
          } catch (e) {
            console.error(`Failed to load data from ${url}:`, e);
          }
        }
        
        if (!data) {
          throw new Error('Unable to load scenario data. Please check your network connection or refresh the page');
        }
        
        // Extract categories and corresponding scenarios
        // eslint-disable-next-line no-unused-vars
        const categoriesData = data.categories || [];
        const scenariosData = data.scenarios || [];
        
        // Organize according to 1101.md category structure
        const workCatsGroup = {
          'workplace_relationships': { id: 'workplace_relationships', name_en: 'Workplace Interpersonal Relationships' },
          'meetings_presentations': { id: 'meetings_presentations', name_en: 'Meetings and Presentations' },
          'career_development': { id: 'career_development', name_en: 'Career Development and Negotiation' },
          'business_etiquette': { id: 'business_etiquette', name_en: 'Business Etiquette' },
          'workplace_challenges': { id: 'workplace_challenges', name_en: 'Workplace Challenges' }
        };
        
        const lifeCatsGroup = {
          'social_activities': { id: 'social_activities', name_en: 'Social Activities' },
          'interpersonal_relationships': { id: 'interpersonal_relationships', name_en: 'Interpersonal Relationships' },
          'difficult_conversations': { id: 'difficult_conversations', name_en: 'Difficult Conversations' },
          'everyday_etiquette': { id: 'everyday_etiquette', name_en: 'Everyday Etiquette' },
          'special_occasions': { id: 'special_occasions', name_en: 'Special Occasions' }
        };
        
        // Get work and life categories
        const workCats = Object.values(workCatsGroup);
        const lifeCats = Object.values(lifeCatsGroup);
        
        setWorkCategories(workCats);
        setLifeCategories(lifeCats);
        
        // Organize scenarios by category
        const scenariosByCat = {};
        
        // Initialize category containers
        Object.values(workCatsGroup).forEach(group => {
          scenariosByCat[group.id] = [];
        });
        
        Object.values(lifeCatsGroup).forEach(group => {
          scenariosByCat[group.id] = [];
        });
        
        // Use title keywords for more precise mapping
        scenariosData.forEach(scenario => {
          const title = scenario.title_en.toLowerCase();
          
          // Work scenario categorization
          if (
            title.includes('team') || 
            title.includes('colleague') || 
            title.includes('boss') || 
            title.includes('superviso') || 
            title.includes('workplace') || 
            title.includes('cultural') ||
            title.includes('office conflict') ||
            (title.includes('interpersonal') && (
              scenario.category_id.includes('work') || 
              scenario.category_id.includes('professional') ||
              scenario.category_id.includes('office')
            ))
          ) {
            scenariosByCat['workplace_relationships'].push(scenario);
          }
          else if (
            title.includes('meeting') || 
            title.includes('presentation') || 
            title.includes('speech') || 
            title.includes('public speak') || 
            title.includes('facilitat') || 
            title.includes('deliver') ||
            title.includes('questioning') ||
            title.includes('discussion')
          ) {
            scenariosByCat['meetings_presentations'].push(scenario);
          }
          else if (
            title.includes('career') || 
            title.includes('job') || 
            title.includes('salary') || 
            title.includes('promotion') || 
            title.includes('interview') || 
            title.includes('negotiat') ||
            title.includes('feedback') ||
            title.includes('performance')
          ) {
            scenariosByCat['career_development'].push(scenario);
          }
          else if (
            title.includes('etiquette') && (
              scenario.category_id.includes('business') ||
              scenario.category_id.includes('work')
            ) ||
            title.includes('business lunch') || 
            title.includes('business gift') || 
            title.includes('dress code') || 
            title.includes('business card') || 
            title.includes('international business')
          ) {
            scenariosByCat['business_etiquette'].push(scenario);
          }
          else if (
            title.includes('criticism') || 
            title.includes('politics') || 
            title.includes('gossip') || 
            title.includes('client') || 
            title.includes('burnout') || 
            (title.includes('conflict') && scenario.category_id.includes('work')) ||
            title.includes('workload') ||
            title.includes('dilemma') ||
            title.includes('workplace bias')
          ) {
            scenariosByCat['workplace_challenges'].push(scenario);
          }
          // Life scenario categorization
          else if (
            title.includes('party') || 
            title.includes('meeting new') || 
            title.includes('meeting people') || 
            title.includes('small talk') || 
            title.includes('online social') || 
            title.includes('community') ||
            title.includes('social media') ||
            (title.includes('conversation') && !title.includes('difficult')) ||
            title.includes('social activit')
          ) {
            scenariosByCat['social_activities'].push(scenario);
          }
          else if (
            (title.includes('relationship') && !scenario.category_id.includes('work')) || 
            title.includes('friend') || 
            title.includes('family') || 
            title.includes('neighbor') || 
            title.includes('stranger') || 
            title.includes('toxic') ||
            title.includes('friendship') ||
            title.includes('parent') ||
            title.includes('personal')
          ) {
            scenariosByCat['interpersonal_relationships'].push(scenario);
          }
          else if (
            title.includes('declining') || 
            title.includes('disagree') || 
            title.includes('apolog') || 
            title.includes('emotion') || 
            title.includes('sensitive') || 
            title.includes('boundary') ||
            (title.includes('feedback') && !scenario.category_id.includes('work')) ||
            title.includes('difficult conversation') ||
            (title.includes('misunderstanding') && !scenario.category_id.includes('work'))
          ) {
            scenariosByCat['difficult_conversations'].push(scenario);
          }
          else if (
            (title.includes('etiquette') && !scenario.category_id.includes('business')) || 
            title.includes('dining') || 
            title.includes('public') || 
            (title.includes('gift') && !scenario.category_id.includes('business')) || 
            title.includes('phone') || 
            (title.includes('email') && !scenario.category_id.includes('business')) ||
            title.includes('transportation') ||
            title.includes('respect') ||
            title.includes('punctuality')
          ) {
            scenariosByCat['everyday_etiquette'].push(scenario);
          }
          else if (
            title.includes('wedding') || 
            title.includes('funeral') || 
            title.includes('formal') || 
            title.includes('holiday') || 
            title.includes('religious') || 
            title.includes('cultural celebration') ||
            title.includes('hosting') ||
            title.includes('party') ||
            title.includes('celebration')
          ) {
            scenariosByCat['special_occasions'].push(scenario);
          }
          // 后备分类 - 根据场景的主要类别
          else {
            // 工作相关场景默认分配到工作挑战
            if (
              scenario.category_id.includes('work') || 
              scenario.category_id.includes('office') || 
              scenario.category_id.includes('professional') || 
              scenario.category_id.includes('business')
            ) {
              scenariosByCat['workplace_challenges'].push(scenario);
            } 
            // 其他场景默认分配到社交活动
            else {
              scenariosByCat['social_activities'].push(scenario);
            }
          }
        });
        
        // 补充 - 如果分类中没有数据，添加样例数据
        Object.keys(scenariosByCat).forEach(categoryId => {
          if ((scenariosByCat[categoryId].length === 0) && (scenariosData.length > 0)) {
            // 添加几个样例场景到空分类
            const sampleScenarios = scenariosData.slice(0, 3);
            sampleScenarios.forEach(scenario => {
              const clonedScenario = { ...scenario };
              
              // 修改标题以匹配分类
              if (categoryId === 'workplace_relationships') {
                clonedScenario.title_en = "Team Conflict Resolution";
                clonedScenario.id = "sample_" + categoryId + "_" + scenario.id;
              } else if (categoryId === 'meetings_presentations') {
                clonedScenario.title_en = "Effective Presentation Skills";
                clonedScenario.id = "sample_" + categoryId + "_" + scenario.id;
              } else if (categoryId === 'career_development') {
                clonedScenario.title_en = "Salary Negotiation Strategies";
                clonedScenario.id = "sample_" + categoryId + "_" + scenario.id;
              } else if (categoryId === 'business_etiquette') {
                clonedScenario.title_en = "Business Lunch Protocol";
                clonedScenario.id = "sample_" + categoryId + "_" + scenario.id;
              } else if (categoryId === 'workplace_challenges') {
                clonedScenario.title_en = "Handling Office Politics";
                clonedScenario.id = "sample_" + categoryId + "_" + scenario.id;
              } else if (categoryId === 'social_activities') {
                clonedScenario.title_en = "Navigating Social Gatherings";
                clonedScenario.id = "sample_" + categoryId + "_" + scenario.id;
              } else if (categoryId === 'interpersonal_relationships') {
                clonedScenario.title_en = "Building Meaningful Friendships";
                clonedScenario.id = "sample_" + categoryId + "_" + scenario.id;
              } else if (categoryId === 'difficult_conversations') {
                clonedScenario.title_en = "Addressing Sensitive Topics";
                clonedScenario.id = "sample_" + categoryId + "_" + scenario.id;
              } else if (categoryId === 'everyday_etiquette') {
                clonedScenario.title_en = "Modern Dining Etiquette";
                clonedScenario.id = "sample_" + categoryId + "_" + scenario.id;
              } else if (categoryId === 'special_occasions') {
                clonedScenario.title_en = "Hosting Successful Celebrations";
                clonedScenario.id = "sample_" + categoryId + "_" + scenario.id;
              }
              
              scenariosByCat[categoryId].push(clonedScenario);
            });
          }
        });
        
        // 确保special_occasions类别只显示第一个场景
        if (scenariosByCat['special_occasions'] && scenariosByCat['special_occasions'].length > 1) {
          // 只保留第一个场景
          scenariosByCat['special_occasions'] = [scenariosByCat['special_occasions'][0]];
        }
        
        setScenariosByCategory(scenariosByCat);
        
        // 设置第一个分类为激活状态
        if (workCats.length > 0) {
          setActiveCategory(workCats[0].id);
        }
      } catch (err) {
        console.error("Error loading scenario list:", err);
        setError('Failed to load scenarios. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    }
    
    loadScenarios();
  }, []);
  
  // 点击分类导航时滚动到对应区域
  const scrollToCategory = (categoryId) => {
    setActiveCategory(categoryId);
    if (categoryRefs.current[categoryId]) {
      categoryRefs.current[categoryId].scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  
  // 将canAccessScenario添加ESLint禁用注释
  // eslint-disable-next-line no-unused-vars
  const canAccessScenario = (scenario) => {
    // 始终允许访问所有场景，不再检查用户权限
    return true;
  };
  
  // 获取场景图片
  const getScenarioImage = (scenario) => {
    // 使用当前存在的图片文件
    const imageMap = {
      'presentation': '/assets/images/scenarios/presentation.jpg',
      'team-conflict': '/assets/images/scenarios/team-conflict.jpg',
      'networking': '/assets/images/scenarios/networking.jpg',
      'feedback': '/assets/images/scenarios/feedback.jpg',
      'interview': '/assets/images/scenarios/interview.jpg',
      'public-speaking': '/assets/images/scenarios/public-speaking.jpg'
    };
    
    // 根据场景标题或ID选择合适的图片
    const title = scenario.title_en.toLowerCase();
    
    // 优先通过关键词匹配
    if (title.includes('presentation') || title.includes('speech') || title.includes('public speak')) {
      return imageMap['presentation'];
    } else if (title.includes('team') || title.includes('colleague') || title.includes('conflict')) {
      return imageMap['team-conflict'];
    } else if (title.includes('network') || title.includes('social') || title.includes('party')) {
      return imageMap['networking'];
    } else if (title.includes('feedback') || title.includes('criticism')) {
      return imageMap['feedback'];
    } else if (title.includes('interview') || title.includes('job') || title.includes('hire')) {
      return imageMap['interview'];
    } else if (title.includes('public') || title.includes('audience') || title.includes('speech')) {
      return imageMap['public-speaking'];
    }
    
    // 如果没有匹配，基于ID的哈希选择一张图片
    const imageKeys = Object.keys(imageMap);
    const hash = scenario.id.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    const randomIndex = Math.abs(hash) % imageKeys.length;
    return imageMap[imageKeys[randomIndex]];
  };
  
  if (loading) {
    return (
      <div className="loading-indicator">
        <div className="spinner"></div>
        <p>Loading scenarios...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="error-container">
        <h3>Loading Error</h3>
        <p>{error}</p>
        <button className="primary-button" onClick={() => window.location.reload()}>Refresh</button>
      </div>
    );
  }
  
  return (
    <div className="advanced-scenarios-container">
      <div className="hero-section">
        <h1 className="hero-title">More Scenarios</h1>
        <p className="hero-subtitle">Real-world situations with expert advice to enhance your social intelligence</p>
      </div>
      
      {/* 导航区域 */}
      <div className="category-nav-container">
        <div className="category-column">
          <h2 className="category-column-title">Work</h2>
          <div className="category-tabs">
            {workCategories.map(category => (
              <button
                key={category.id}
                className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => scrollToCategory(category.id)}
              >
                {category.name_en}
              </button>
            ))}
          </div>
        </div>
        
        <div className="category-column">
          <h2 className="category-column-title">Life</h2>
          <div className="category-tabs">
            {lifeCategories.map(category => (
              <button
                key={category.id}
                className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => scrollToCategory(category.id)}
              >
                {category.name_en}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* 场景分类和列表 */}
      <div className="scenarios-content">
        {/* 工作场景部分 */}
        <div className="scenarios-section">
          <h2 className="section-group-title">Work Social Scenarios</h2>
          
          {workCategories.map(category => (
            <div 
              key={category.id} 
              ref={el => categoryRefs.current[category.id] = el}
              className="category-section"
              id={`category-${category.id}`}
            >
              <h3 className="category-title">{category.name_en}</h3>
              
              {(scenariosByCategory[category.id]?.length > 0) ? (
                <div className="scenario-grid">
                  {scenariosByCategory[category.id]?.map(scenario => {
                    const tags = getScenarioTags(scenario);
                    // 将这两个变量设为false，确保卡片正常显示
                    const showFreeOnly = false;
                    const showPremiumOnly = false;
                    const item = {isPremium: false}; // 这里使用scenario的实际属性
                    
                    // eslint-disable-next-line no-mixed-operators
                    if ((item.isPremium && showFreeOnly) || (!item.isPremium && showPremiumOnly)) {
                      return null;
                    }
                    
                    return (
                    <div key={scenario.id} className="scenario-card">
                      <div className="scenario-card-image">
                        <img 
                          src={getScenarioImage(scenario)} 
                          alt={scenario.title_en} 
                          onError={(e) => {
                            e.target.src = '/assets/images/scenarios/default.jpg';
                            e.target.onerror = () => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22348%22%20height%3D%22225%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20348%20225%22%20preserveAspectRatio%3D%22none%22%3E%3Cdefs%3E%3Cstyle%20type%3D%22text%2Fcss%22%3E%23holder_189e178ef62%20text%20%7B%20fill%3A%23eceeef%3Bfont-weight%3Abold%3Bfont-family%3AArial%2C%20Helvetica%2C%20Open%20Sans%2C%20sans-serif%2C%20monospace%3Bfont-size%3A17pt%20%7D%20%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cg%20id%3D%22holder_189e178ef62%22%3E%3Crect%20width%3D%22348%22%20height%3D%22225%22%20fill%3D%22%2355595c%22%3E%3C%2Frect%3E%3Cg%3E%3Ctext%20x%3D%22116.71875%22%20y%3D%22120.3%22%3EScenario%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';
                            };
                          }}
                        />
                        <h3 className="scenario-title">{scenario.title_en}</h3>
                      </div>
                      <div className="scenario-card-content">
                        <div className="scenario-tags">
                          <span className="profession-tag">{tags.profession}</span>
                          <span className="context-tag">{tags.context}</span>
                          <span className={`difficulty-tag ${tags.difficulty.toLowerCase()}`}>{tags.difficulty}</span>
                        </div>
                        <Link to={`/advanced-scenarios/${scenario.id}`} className="scenario-link">
                          View Details
                        </Link>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-scenarios">
                  <p>No scenarios found in this category.</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* 生活场景部分 */}
        <div className="scenarios-section">
          <h2 className="section-group-title">Life Social Scenarios</h2>
          
          {lifeCategories.map(category => (
            <div 
              key={category.id} 
              ref={el => categoryRefs.current[category.id] = el}
              className="category-section"
              id={`category-${category.id}`}
            >
              <h3 className="category-title">{category.name_en}</h3>
              
              {(scenariosByCategory[category.id]?.length > 0) ? (
                <div className="scenario-grid">
                  {scenariosByCategory[category.id]?.map(scenario => {
                    const tags = getScenarioTags(scenario);
                    // 将这两个变量设为false，确保卡片正常显示
                    const showFreeOnly = false;
                    const showPremiumOnly = false;
                    const item = {isPremium: false}; // 这里使用scenario的实际属性
                    
                    // eslint-disable-next-line no-mixed-operators
                    if ((item.isPremium && showFreeOnly) || (!item.isPremium && showPremiumOnly)) {
                      return null;
                    }
                    
                    return (
                    <div key={scenario.id} className="scenario-card">
                      <div className="scenario-card-image">
                        <img 
                          src={getScenarioImage(scenario)} 
                          alt={scenario.title_en} 
                          onError={(e) => {
                            e.target.src = '/assets/images/scenarios/default.jpg';
                            e.target.onerror = () => {
                              e.target.onerror = null;
                              e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22348%22%20height%3D%22225%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20348%20225%22%20preserveAspectRatio%3D%22none%22%3E%3Cdefs%3E%3Cstyle%20type%3D%22text%2Fcss%22%3E%23holder_189e178ef62%20text%20%7B%20fill%3A%23eceeef%3Bfont-weight%3Abold%3Bfont-family%3AArial%2C%20Helvetica%2C%20Open%20Sans%2C%20sans-serif%2C%20monospace%3Bfont-size%3A17pt%20%7D%20%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cg%20id%3D%22holder_189e178ef62%22%3E%3Crect%20width%3D%22348%22%20height%3D%22225%22%20fill%3D%22%2355595c%22%3E%3C%2Frect%3E%3Cg%3E%3Ctext%20x%3D%22116.71875%22%20y%3D%22120.3%22%3EScenario%3C%2Ftext%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E';
                            };
                          }}
                        />
                        <h3 className="scenario-title">{scenario.title_en}</h3>
                      </div>
                      <div className="scenario-card-content">
                        <div className="scenario-tags">
                          <span className="profession-tag">{tags.profession}</span>
                          <span className="context-tag">{tags.context}</span>
                          <span className={`difficulty-tag ${tags.difficulty.toLowerCase()}`}>{tags.difficulty}</span>
                        </div>
                        <Link to={`/advanced-scenarios/${scenario.id}`} className="scenario-link">
                          View Details
                        </Link>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-scenarios">
                  <p>No scenarios found in this category.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AdvancedScenarioList; 